import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { settingsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { ArrowLeft, Volume2, User, Trash2, Shield, LogOut, Heart, AlertTriangle } from 'lucide-react';

const Settings = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [settings, setSettings] = useState({
    voice_enabled: true,
    avatar_enabled: true,
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings(response.data);
    } catch (error) {
      console.error('Load settings error:', error);
    }
  };

  const updateSetting = async (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    setIsSaving(true);
    try {
      await settingsAPI.update(newSettings);
    } catch (error) {
      console.error('Update settings error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteHistory = async () => {
    try {
      await settingsAPI.deleteHistory();
    } catch (error) {
      console.error('Delete history error:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background grain">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="container mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center">
          <Button variant="ghost" size="icon" onClick={() => navigate('/chat')} className="rounded-full h-9 w-9 sm:h-10 sm:w-10" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </Button>
          <h1 className="text-lg sm:text-xl font-heading font-bold ml-3 sm:ml-4">Settings</h1>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 max-w-2xl space-y-4 sm:space-y-6">
        {/* Profile Card */}
        <Card className="shadow-card border-0">
          <CardHeader className="p-4 sm:p-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <CardTitle className="text-base sm:text-lg truncate">{user?.name || 'Guest User'}</CardTitle>
                <CardDescription className="text-xs sm:text-sm truncate">{user?.email}</CardDescription>
                {user?.is_guest && (
                  <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-accent/10 text-accent text-xs font-medium">
                    Guest Account
                  </span>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Preferences */}
        <Card className="shadow-card border-0">
          <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Volume2 className="w-4 h-4 sm:w-5 sm:h-5" />
              Preferences
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 sm:space-y-6 p-4 sm:p-6 pt-0">
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5 min-w-0">
                <Label htmlFor="voice" className="text-sm">Voice Responses</Label>
                <p className="text-xs sm:text-sm text-muted-foreground">Enable AI voice responses</p>
              </div>
              <Switch
                id="voice"
                checked={settings.voice_enabled}
                onCheckedChange={(checked) => updateSetting('voice_enabled', checked)}
                data-testid="voice-switch"
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5 min-w-0">
                <Label htmlFor="avatar" className="text-sm">AI Avatar</Label>
                <p className="text-xs sm:text-sm text-muted-foreground">Show animated avatar in chat</p>
              </div>
              <Switch
                id="avatar"
                checked={settings.avatar_enabled}
                onCheckedChange={(checked) => updateSetting('avatar_enabled', checked)}
                data-testid="avatar-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Data & Privacy */}
        <Card className="shadow-card border-0">
          <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Shield className="w-4 h-4 sm:w-5 sm:h-5" />
              Data & Privacy
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 p-4 sm:p-6 pt-0">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="w-full justify-start rounded-xl text-destructive hover:text-destructive text-sm" data-testid="delete-history-btn">
                  <Trash2 className="w-4 h-4 mr-2 sm:mr-3 flex-shrink-0" />
                  <span className="truncate">Delete Conversation History</span>
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="max-w-[90vw] sm:max-w-lg mx-4">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-base sm:text-lg">Delete All Conversations?</AlertDialogTitle>
                  <AlertDialogDescription className="text-sm">
                    This will permanently delete all your chat history and session summaries. 
                    This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                  <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleDeleteHistory} 
                    className="rounded-full bg-destructive hover:bg-destructive/90"
                    data-testid="confirm-delete-btn"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>

        {/* Disclaimer */}
        <Card className="shadow-card border-0 bg-muted/30">
          <CardHeader className="p-4 sm:p-6 pb-2">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
              <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              Important Reminder
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4 text-xs sm:text-sm text-muted-foreground p-4 sm:p-6 pt-0">
            <p>
              TPOCARE AI is designed to provide emotional support and companionship. 
              It is <strong>not</strong> a replacement for professional mental health care.
            </p>
            <ul className="space-y-1.5 sm:space-y-2">
              <li className="flex gap-2">
                <span className="text-primary">•</span>
                This AI does not diagnose or treat mental health conditions
              </li>
              <li className="flex gap-2">
                <span className="text-primary">•</span>
                Please consult a licensed professional for medical advice
              </li>
              <li className="flex gap-2">
                <span className="text-primary">•</span>
                In crisis situations, contact emergency services
              </li>
            </ul>
            
            <div className="p-3 sm:p-4 rounded-xl bg-accent/10 border border-accent/20">
              <p className="flex items-center gap-2 font-medium text-foreground text-xs sm:text-sm">
                <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4 text-accent" />
                Crisis Resources
              </p>
              <ul className="mt-1.5 sm:mt-2 space-y-0.5 sm:space-y-1 text-xs">
                <li><strong>988 Lifeline:</strong> Call or text 988</li>
                <li><strong>Crisis Text:</strong> Text HOME to 741741</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Logout */}
        <Button 
          variant="outline" 
          className="w-full rounded-xl text-destructive hover:text-destructive" 
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-3" />
          Logout
        </Button>

        <p className="text-center text-xs text-muted-foreground">
          TPOCARE AI v1.0 • You're not alone. We're here to listen.
        </p>
      </main>
    </div>
  );
};

export default Settings;
